import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { ArrowLeft, TrendingUp, Users, FileCheck2, Wallet, Target } from "lucide-react";

/*
 * AUPRÉA — Admin · Suivi des conversions
 * Dashboard funnel bilan → compte → carnet → tarifs → checkout → payant.
 * Chaque indicateur vient de la base produit (jamais de Brevo).
 */

const C = {
  navy: "#15294f",
  gold: "#caa86a",
  cream: "#f3efe6",
  ink: "#23262e",
  green: "#2f7d5b",
  line: "rgba(21,41,79,0.1)",
};

const PERIODS = [
  { id: "7d", label: "7 jours" },
  { id: "30d", label: "30 jours" },
  { id: "all", label: "Tout" },
];

function Card({ icon, label, value, hint, testid, accent = C.navy }) {
  return (
    <div data-testid={testid} style={{
      background: "#fff", border: `1px solid ${C.line}`, padding: "20px 22px",
      display: "flex", flexDirection: "column", gap: 6, minHeight: 110,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ color: accent, display: "inline-flex" }}>{icon}</span>
        <span style={{ fontSize: 11.5, letterSpacing: "0.14em", color: "rgba(35,38,46,0.55)", fontWeight: 700, textTransform: "uppercase" }}>{label}</span>
      </div>
      <div style={{ fontFamily: "'Spectral', serif", fontSize: 36, fontWeight: 500, color: C.navy, lineHeight: 1.1 }}>{value}</div>
      {hint && <div style={{ fontSize: 12.5, color: "rgba(35,38,46,0.6)" }}>{hint}</div>}
    </div>
  );
}

function Rate({ label, value, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${C.line}` }}>
      <span style={{ fontSize: 14, color: C.ink }}>{label}</span>
      <span style={{ fontFamily: "'Spectral', serif", fontSize: 20, fontWeight: 500, color: C.navy }}>{value.toFixed(1)} %</span>
    </div>
  );
}

export default function AdminConversions() {
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "AUPRÉA Direction · Suivi des conversions";
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    api.get(`/admin/conversions?period=${period}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || "Erreur de chargement."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period]);

  const fmt = (n) => (n ?? 0).toLocaleString("fr-FR");
  const fmtEur = (n) => `${(n ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <div style={{ background: C.cream, minHeight: "100vh", padding: "40px 32px", fontFamily: "'Archivo', system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
          <div>
            <Link to="/admin" style={{ color: C.navy, fontSize: 14, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 8 }} data-testid="back-to-admin">
              <ArrowLeft size={16} /> Retour direction
            </Link>
            <h1 style={{ fontFamily: "'Spectral', serif", fontSize: 34, color: C.navy, fontWeight: 500, margin: 0, letterSpacing: "-0.01em" }}>
              Suivi des conversions
            </h1>
            <p style={{ fontSize: 15, color: "rgba(35,38,46,0.65)", margin: "6px 0 0 0" }}>
              Funnel complet : bilan → compte → carnet → tarifs → paiement.
            </p>
          </div>

          <div style={{ display: "flex", gap: 2, background: "rgba(21,41,79,0.08)", padding: 4 }} data-testid="period-switcher">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
                style={{
                  padding: "9px 20px", fontSize: 13.5, fontWeight: 600,
                  background: period === p.id ? C.navy : "transparent",
                  color: period === p.id ? C.cream : C.navy,
                  border: "none", cursor: "pointer", letterSpacing: "0.02em",
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading && <div data-testid="conversions-loading" style={{ padding: 40, textAlign: "center", color: "rgba(35,38,46,0.6)" }}>Chargement…</div>}
        {error && <div data-testid="conversions-error" style={{ padding: 20, background: "#fcf3f3", border: "1px solid #f0c5c5", color: "#b53a3a", marginBottom: 20 }}>{error}</div>}

        {data && !loading && (
          <div data-testid="conversions-dashboard">
            {/* MRR en haut en évidence */}
            <div style={{ background: C.navy, color: C.cream, padding: "28px 32px", marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }} data-testid="mrr-highlight">
              <div>
                <div style={{ fontSize: 11, letterSpacing: "0.22em", color: C.gold, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>MRR Actuel</div>
                <div style={{ fontFamily: "'Spectral', serif", fontSize: 42, fontWeight: 500, lineHeight: 1 }}>{fmtEur(data.mrr_eur)}</div>
                <div style={{ fontSize: 13, color: "rgba(243,239,230,0.65)", marginTop: 6 }}>
                  {data.clients_payants.total} client{data.clients_payants.total > 1 ? "s" : ""} payant{data.clients_payants.total > 1 ? "s" : ""} actifs
                </div>
              </div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {Object.entries(data.clients_payants.by_plan).map(([pid, count]) => (
                  <div key={pid} data-testid={`plan-count-${pid}`} style={{ textAlign: "center", minWidth: 90 }}>
                    <div style={{ fontSize: 10.5, color: C.gold, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                      {pid === "essentiel" ? "9,99" : pid === "confort" ? "19,90" : "29,99"} €
                    </div>
                    <div style={{ fontFamily: "'Spectral', serif", fontSize: 24, fontWeight: 500 }}>{count}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Funnel : grid 4 colonnes */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
              <Card icon={<FileCheck2 size={18} />} label="Bilans complétés" value={fmt(data.bilans.total)}
                    hint={`${fmt(data.bilans.with_consent)} avec consentement`} testid="card-bilans" />
              <Card icon={<Users size={18} />} label="Comptes gratuits créés" value={fmt(data.comptes.created)}
                    hint={`${fmt(data.comptes.with_consent)} avec consentement`} testid="card-comptes" />
              <Card icon={<TrendingUp size={18} />} label="Carnets ≥ 50 %" value={fmt(data.carnets.ge_50_percent)}
                    hint={`${fmt(data.carnets.complete_100)} complets à 100 %`} testid="card-carnets" accent={C.green} />
              <Card icon={<Target size={18} />} label="Visites page Tarifs" value={fmt(data.tarifs_visits)}
                    hint={`${fmt(data.checkouts_started)} checkout démarrés`} testid="card-tarifs" accent={C.gold} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 32 }}>
              <Card icon={<Wallet size={18} />} label="Clients payants" value={fmt(data.clients_payants.total)}
                    hint={`Essentiel ${data.clients_payants.by_plan.essentiel} · Confort ${data.clients_payants.by_plan.confort} · Patrimoine+ ${data.clients_payants.by_plan.patrimoine_plus}`}
                    testid="card-payants" accent={C.gold} />
            </div>

            {/* Taux de conversion */}
            <div style={{ background: "#fff", border: `1px solid ${C.line}`, padding: "24px 28px" }} data-testid="rates-panel">
              <div style={{ fontSize: 11, letterSpacing: "0.22em", color: C.gold, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>— Taux de conversion</div>
              <h2 style={{ fontFamily: "'Spectral', serif", fontSize: 22, color: C.navy, fontWeight: 500, margin: "0 0 16px 0" }}>Entre chaque étape</h2>
              <Rate label="Bilan → consentement marketing" value={data.rates.bilan_to_consent} testid="rate-bilan-consent" />
              <Rate label="Bilan → compte gratuit créé" value={data.rates.bilan_to_compte} testid="rate-bilan-compte" />
              <Rate label="Compte → carnet rempli à 50 %" value={data.rates.compte_to_carnet50} testid="rate-compte-c50" />
              <Rate label="Carnet 50 % → carnet 100 %" value={data.rates.carnet50_to_carnet100} testid="rate-c50-c100" />
              <Rate label="Compte → visite page Tarifs" value={data.rates.compte_to_tarifs} testid="rate-compte-tarifs" />
              <Rate label="Tarifs → checkout démarré" value={data.rates.tarifs_to_checkout} testid="rate-tarifs-checkout" />
              <Rate label="Checkout → paiement abouti" value={data.rates.checkout_to_payant} testid="rate-checkout-payant" />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 16px 0", marginTop: 8 }}>
                <span style={{ fontSize: 15, color: C.navy, fontWeight: 600 }}>Taux global : Bilan → Client payant</span>
                <span style={{ fontFamily: "'Spectral', serif", fontSize: 28, fontWeight: 500, color: C.gold }} data-testid="rate-global">{data.rates.global_bilan_to_payant.toFixed(1)} %</span>
              </div>
            </div>

            <div style={{ marginTop: 24, fontSize: 12.5, color: "rgba(35,38,46,0.55)" }}>
              Période analysée : <strong>{PERIODS.find((p) => p.id === data.period)?.label || data.period}</strong>.
              Les indicateurs sont calculés en direct à partir de la base produit (Mongo). Les clients payants et le MRR sont instantanés (indépendants de la période).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
